/**
 * PW recommended products — Search & Discovery + carousel.
 */
class PwProductRecommendations extends HTMLElement {
  connectedCallback() {
    if (this.dataset.loaded === 'true') return;
    this.load();
  }

  load() {
    const productId = this.dataset.productId;
    const sectionId = this.dataset.sectionId;
    const url = this.dataset.url;

    if (!productId || !sectionId || !url) return;

    fetch(`${url}&product_id=${productId}&section_id=${sectionId}`)
      .then((response) => response.text())
      .then((text) => {
        const html = document.createElement('div');
        html.innerHTML = text;
        const source =
          html.querySelector('[data-pw-recommendations-api]') ||
          html.querySelector('#shopify-section-pw-recommended-products [data-pw-recommendations-api]');

        if (source?.innerHTML.trim().length) {
          this.innerHTML = source.innerHTML;
        }

        this.dataset.loaded = 'true';
        this.dispatchEvent(
          new CustomEvent('pw-recommendations-loaded', {
            bubbles: true,
            detail: { count: this.querySelectorAll('.pw-recommended-product-card').length },
          })
        );
      })
      .catch((error) => {
        console.error('PW recommended products:', error);
        this.dispatchEvent(new CustomEvent('pw-recommendations-loaded', { bubbles: true, detail: { count: 0 } }));
      });
  }
}

if (!customElements.get('pw-product-recommendations')) {
  customElements.define('pw-product-recommendations', PwProductRecommendations);
}

class PwRecommendedCarousel {
  constructor(section) {
    this.section = section;
    this.viewport = section.querySelector('.pw-recommended-products__viewport');
    this.host = section.querySelector('pw-product-recommendations');
    this.prevBtn = section.querySelector('[data-pw-recommended-prev]');
    this.nextBtn = section.querySelector('[data-pw-recommended-next]');
    this.track = null;
    this.slides = [];
    this.index = 0;
    this.resizeObserver = null;

    this.onPrev = () => this.goTo(this.index - 1);
    this.onNext = () => this.goTo(this.index + 1);
    this.onResize = () => this.refresh(false);

    this.prevBtn?.addEventListener('click', this.onPrev);
    this.nextBtn?.addEventListener('click', this.onNext);
  }

  getVisibleCount() {
    const mobile = window.matchMedia('(max-width: 749px)').matches;
    const raw = mobile
      ? this.section.dataset.slidesVisibleMobile || this.section.style.getPropertyValue('--pw-slides-visible-mobile')
      : this.section.dataset.slidesVisible || this.section.style.getPropertyValue('--pw-slides-visible');
    const parsed = parseInt(String(raw).trim(), 10);
    return Number.isFinite(parsed) && parsed > 0 ? parsed : 1;
  }

  bindTrack() {
    this.track = this.host?.querySelector('.pw-recommended-products__track');
    if (!this.track) return false;

    this.slides = Array.from(this.track.querySelectorAll('[role="listitem"]'));
    if (!this.track.id) {
      this.track.id = `PwRecommendedTrack-${this.section.id || 'pw'}`;
    }
    this.prevBtn?.setAttribute('aria-controls', this.track.id);
    this.nextBtn?.setAttribute('aria-controls', this.track.id);
    return this.slides.length > 0;
  }

  getMaxIndex() {
    return Math.max(0, this.slides.length - this.getVisibleCount());
  }

  goTo(nextIndex) {
    if (!this.track || !this.slides.length) return;
    this.index = Math.max(0, Math.min(nextIndex, this.getMaxIndex()));
    const slide = this.slides[0];
    const gap = parseFloat(getComputedStyle(this.track).columnGap || getComputedStyle(this.track).gap) || 12;
    const slideWidth = slide.getBoundingClientRect().width;
    const offset = this.index * (slideWidth + gap);
    this.track.style.transform = `translate3d(-${offset}px, 0, 0)`;
    this.updateArrows();
  }

  updateArrows() {
    const maxIndex = this.getMaxIndex();
    const canSlide = maxIndex > 0;

    if (this.prevBtn) {
      this.prevBtn.disabled = !canSlide || this.index <= 0;
      this.prevBtn.classList.toggle('is-disabled', this.prevBtn.disabled);
    }
    if (this.nextBtn) {
      this.nextBtn.disabled = !canSlide || this.index >= maxIndex;
      this.nextBtn.classList.toggle('is-disabled', this.nextBtn.disabled);
    }
  }

  refresh(resetIndex = true) {
    if (!this.bindTrack()) return;
    if (resetIndex) this.index = 0;
    if (this.index > this.getMaxIndex()) this.index = this.getMaxIndex();
    this.track.style.transform = '';
    requestAnimationFrame(() => this.goTo(this.index));
  }

  init() {
    if (!this.bindTrack()) return;

    this.refresh(true);

    if (this.resizeObserver) this.resizeObserver.disconnect();
    this.resizeObserver = new ResizeObserver(() => this.refresh(false));
    this.resizeObserver.observe(this.viewport);
    window.addEventListener('resize', this.onResize);
  }

  destroy() {
    this.prevBtn?.removeEventListener('click', this.onPrev);
    this.nextBtn?.removeEventListener('click', this.onNext);
    window.removeEventListener('resize', this.onResize);
    this.resizeObserver?.disconnect();
  }
}

(function () {
  const SELECTOR = '[data-pw-recommended-products]';
  const carousels = new WeakMap();

  function updateSection(section, detail) {
    const host = section.querySelector('pw-product-recommendations');
    const count = detail?.count ?? host?.querySelectorAll('.pw-recommended-product-card').length ?? 0;
    const hasProducts = count > 0;

    section.classList.remove('is-loading');
    section.classList.toggle('pw-recommended-products--loaded', hasProducts);
    section.classList.toggle('pw-recommended-products--empty', !hasProducts);

    if (hasProducts) {
      let carousel = carousels.get(section);
      if (!carousel) {
        carousel = new PwRecommendedCarousel(section);
        carousels.set(section, carousel);
      }
      carousel.init();
    }
  }

  function initSection(section) {
    section.addEventListener('pw-recommendations-loaded', (event) => {
      updateSection(section, event.detail);
    });
  }

  function init() {
    document.querySelectorAll(SELECTOR).forEach(initSection);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();
